import React, { useMemo, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";

/* Subtle shared cinematic backdrop — drifting starfield + faint orbital rings.
   Sits behind data screens (Intel / Life) so the whole command center feels unified. */
function Stars({ colorHex }) {
  const ref = useRef();
  const geo = useMemo(() => {
    const N = 600, pos = new Float32Array(N * 3);
    for (let i = 0; i < N; i++) {
      pos[i * 3] = (Math.random() - 0.5) * 22;
      pos[i * 3 + 1] = (Math.random() - 0.5) * 14;
      pos[i * 3 + 2] = (Math.random() - 0.5) * 10 - 3;
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    return g;
  }, []);
  useFrame((st, dt) => { if (ref.current) ref.current.rotation.y += dt * 0.02; });
  return (
    <points ref={ref} geometry={geo}>
      <pointsMaterial color={colorHex} size={0.03} transparent opacity={0.55} sizeAttenuation depthWrite={false} blending={THREE.AdditiveBlending} />
    </points>
  );
}

function FaintRings({ colorHex }) {
  const a = useRef(), b = useRef();
  useFrame((st, dt) => {
    if (a.current) a.current.rotation.z += dt * 0.08;
    if (b.current) { b.current.rotation.x += dt * 0.05; b.current.rotation.y += dt * 0.03; }
  });
  return (
    <group position={[0, 0, -2]}>
      <mesh ref={a} rotation={[Math.PI / 2.4, 0, 0]}>
        <torusGeometry args={[6, 0.01, 12, 200]} />
        <meshBasicMaterial color={colorHex} transparent opacity={0.12} blending={THREE.AdditiveBlending} />
      </mesh>
      <mesh ref={b} rotation={[0, Math.PI / 3, Math.PI / 5]}>
        <torusGeometry args={[8, 0.008, 12, 200]} />
        <meshBasicMaterial color={colorHex} transparent opacity={0.08} blending={THREE.AdditiveBlending} />
      </mesh>
    </group>
  );
}

export default function NexusBackdrop({ colorHex = "#00F0FF" }) {
  return (
    <div className="fixed inset-0 pointer-events-none" style={{ zIndex: 0 }} data-testid="nexus-backdrop-3d" aria-hidden>
      <Canvas dpr={[1, 1.5]} camera={{ position: [0, 0, 9], fov: 55 }} gl={{ antialias: true, alpha: true }} style={{ width: "100%", height: "100%" }}>
        <Stars colorHex={colorHex} />
        <FaintRings colorHex={colorHex} />
      </Canvas>
    </div>
  );
}
